import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  Output,
  EventEmitter,
} from '@angular/core';
import * as SPLAT from 'gsplat';

@Component({
  selector: 'lib-generated-object-representation',
  templateUrl: './generated-object-representation.component.html',
  styleUrls: ['./generated-object-representation.component.scss'],
  standalone: false,
})
export class GeneratedObjectRepresentationComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Output() closeGeneratedObjectModal = new EventEmitter<void>();
  @Input() modelUrl: string | null = null;
  @ViewChild('rendererContainer', { static: true })
  rendererContainer!: ElementRef<HTMLDivElement>;

  private renderer!: SPLAT.WebGLRenderer;
  private scene!: SPLAT.Scene;
  private camera!: SPLAT.Camera;
  private controls!: SPLAT.OrbitControls;
  private animationFrameId: number | null = null;
  private canvas!: HTMLCanvasElement;
  private axisCanvas!: HTMLCanvasElement;
  private axisCtx!: CanvasRenderingContext2D;
  private isDestroyed = false;
  private initialTheta: number | null = null;
  private previousTheta: number | null = null;

  ngAfterViewInit(): void {
    this.initScene();
    if (this.modelUrl) {
      this.loadModel(this.modelUrl);
    }

    window.addEventListener('keydown', this.disableKeyboardInput, true);
    window.addEventListener('keyup', this.disableKeyboardInput, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modelUrl'] && this.modelUrl) {
      this.loadModel(this.modelUrl);
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('keydown', this.disableKeyboardInput, true);
    window.removeEventListener('keyup', this.disableKeyboardInput, true);

    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    if (this.renderer) this.renderer.dispose();
    if (this.canvas && this.canvas.parentNode)
      this.canvas.parentNode.removeChild(this.canvas);
    if (this.axisCanvas && this.axisCanvas.parentNode)
      this.axisCanvas.parentNode.removeChild(this.axisCanvas);
  }

  disableKeyboardInput = (event: KeyboardEvent): void => {
    const blockedKeys = [
      'w', 'a', 's', 'd', 'q', 'e',
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'
    ];

    const key = event.key;
    if (blockedKeys.includes(key) || blockedKeys.includes(key.toLowerCase())) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  closeModal(){
    this.closeGeneratedObjectModal.emit();
  }

  initScene(): void {
    const container = this.rendererContainer.nativeElement;
    const width = container.clientWidth || 1000;
    const height = container.clientHeight || 850;

    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    this.canvas.style.borderRadius = '12px';
    container.innerHTML = '';
    container.appendChild(this.canvas);

    this.axisCanvas = document.createElement('canvas');
    this.axisCanvas.width = width;
    this.axisCanvas.height = height;
    this.axisCanvas.style.position = 'absolute';
    this.axisCanvas.style.top = '0';
    this.axisCanvas.style.left = '0';
    this.axisCanvas.style.pointerEvents = 'none';
    this.axisCanvas.style.borderRadius = '12px';
    container.style.position = 'relative';
    container.appendChild(this.axisCanvas);
    
    this.axisCtx = this.axisCanvas.getContext('2d')!;

    this.scene = new SPLAT.Scene();
    this.renderer = new SPLAT.WebGLRenderer(this.canvas);
    
    // this.renderer.backgroundColor = new SPLAT.Color32(170, 235, 175, 0.7);
    this.renderer.backgroundColor = new SPLAT.Color32(32, 32, 32, 1);

    this.camera = new SPLAT.Camera();
    this.camera.data.setSize(width, height);

    this.controls = new SPLAT.OrbitControls(this.camera, this.canvas, 2, 2, 2);
    this.controls.maxZoom = 1;
    this.controls.minZoom = 0.5;
    this.controls.dampening = 0.1;
    this.controls.zoomSpeed = 0.1;
    this.controls.orbitSpeed = 0.5;
    this.controls.minAngle = -40;
    this.controls.maxAngle = 40;
    this.controls.setCameraTarget(new SPLAT.Vector3(0, 0, 0));
    this.controls.panSpeed = 0;

    window.addEventListener('resize', this.onWindowResize);

    this.animate();
  }

  private async loadModel(path: string): Promise<void> {
    try {
      if (!path.toLowerCase().endsWith('.splat')) {
        return;
      }

      this.scene = new SPLAT.Scene();
      await SPLAT.Loader.LoadAsync(path, this.scene);

      this.centerCameraOnScene();

    } catch (err) {
      console.error('❌ Error loading model:', err);
    }
  }

  centerCameraOnScene(): void {
    if (!this.scene || this.scene.objects.length === 0) {
      return;
    }

    const splatObject = this.scene.objects[0];

    const positions = (splatObject as any).data?.positions || 
                      (splatObject as any).positions ||
                      (splatObject as any)._data?.positions;

    if (!positions || positions.length === 0) {
      this.camera.position = new SPLAT.Vector3(0, 0, 3);
      this.controls.setCameraTarget(new SPLAT.Vector3(0, 0, 0));
      return;
    }

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const z = positions[i + 2];

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      minZ = Math.min(minZ, z);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
      maxZ = Math.max(maxZ, z);
    }

    if (!isFinite(minX) || !isFinite(maxX)) {
      this.camera.position = new SPLAT.Vector3(0, 0, 3);
      this.controls.setCameraTarget(new SPLAT.Vector3(0, 0, 0));
      return;
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
    const center = new SPLAT.Vector3(centerX, centerY, centerZ);

    const sizeX = maxX - minX;
    const sizeY = maxY - minY;
    const sizeZ = maxZ - minZ;
    const maxSize = Math.max(sizeX, sizeY, sizeZ);

    const distance = maxSize * 2;
    
    this.camera.position = new SPLAT.Vector3(
      centerX,
      centerY,
      centerZ + distance
    );

    this.controls.setCameraTarget(center);
  }

  // drawAxes(): void {
  //   if (!this.axisCtx || !this.camera) return;

  //   const ctx = this.axisCtx;
  //   const width = this.axisCanvas.width;
  //   const height = this.axisCanvas.height;

  //   ctx.clearRect(0, 0, width, height);

  //   const originX = 80;
  //   const originY = height - 80;
  //   const axisLength = 50;

  //   const rotation = (this.camera as any).rotation;
    
  //   if (!rotation) {
  //     this.drawAxesFromViewMatrix(ctx, originX, originY, axisLength);
  //     return;
  //   }

  //   const xAxis = this.rotateVector([1, 0, 0], rotation);
  //   const yAxis = this.rotateVector([0, 1, 0], rotation);
  //   const zAxis = this.rotateVector([0, 0, 1], rotation);

  //   this.drawAxis(ctx, originX, originY, xAxis, axisLength, '#ff0000', 'X');
  //   this.drawAxis(ctx, originX, originY, yAxis, axisLength, '#00ff00', 'Y');
  //   this.drawAxis(ctx, originX, originY, zAxis, axisLength, '#0066ff', 'Z');
  // }

  // drawAxesFromViewMatrix(
  //   ctx: CanvasRenderingContext2D,
  //   originX: number,
  //   originY: number,
  //   axisLength: number
  // ): void {
  //   const viewMatrix = this.camera.data.viewMatrix;
    
  //   const m = (viewMatrix as any).buffer || (viewMatrix as any).elements;
    
  //   if (!m) {
  //     console.warn('Cannot access view matrix elements');
  //     return;
  //   }

  //   const right = [m[0], m[1], m[2]];
  //   const up = [m[4], m[5], m[6]];
  //   const forward = [-m[8], -m[9], -m[10]];

  //   this.drawAxis(ctx, originX, originY, right, axisLength, '#ff0000', 'X');
  //   this.drawAxis(ctx, originX, originY, up, axisLength, '#00ff00', 'Y');
  //   this.drawAxis(ctx, originX, originY, forward, axisLength, '#0066ff', 'Z');
  // }

  // rotateVector(vec: number[], rotation: any): number[] {
  //   const [x, y, z] = vec;
    
  //   if (rotation.x !== undefined) {
  //     const qx = rotation.x;
  //     const qy = rotation.y;
  //     const qz = rotation.z;
  //     const qw = rotation.w;

  //     const ix = qw * x + qy * z - qz * y;
  //     const iy = qw * y + qz * x - qx * z;
  //     const iz = qw * z + qx * y - qy * x;
  //     const iw = -qx * x - qy * y - qz * z;

  //     return [
  //       ix * qw + iw * -qx + iy * -qz - iz * -qy,
  //       iy * qw + iw * -qy + iz * -qx - ix * -qz,
  //       iz * qw + iw * -qz + ix * -qy - iy * -qx
  //     ];
  //   }
    
  //   const [qx, qy, qz, qw] = rotation;
    
  //   const ix = qw * x + qy * z - qz * y;
  //   const iy = qw * y + qz * x - qx * z;
  //   const iz = qw * z + qx * y - qy * x;
  //   const iw = -qx * x - qy * y - qz * z;

  //   return [
  //     ix * qw + iw * -qx + iy * -qz - iz * -qy,
  //     iy * qw + iw * -qy + iz * -qx - ix * -qz,
  //     iz * qw + iw * -qz + ix * -qy - iy * -qx
  //   ];
  // }

  // drawAxis(
  //   ctx: CanvasRenderingContext2D,
  //   originX: number,
  //   originY: number,
  //   direction: number[],
  //   length: number,
  //   color: string,
  //   label: string
  // ): void {
  //   const endX = originX - direction[0] * length;
  //   const endY = originY - direction[1] * length; 

  //   ctx.strokeStyle = color;
  //   ctx.lineWidth = 3;
  //   ctx.beginPath();
  //   ctx.moveTo(originX, originY);
  //   ctx.lineTo(endX, endY);
  //   ctx.stroke();

  //   const arrowSize = 8;
  //   const angle = Math.atan2(-(direction[1]), direction[0]);
  //   ctx.fillStyle = color;
  //   ctx.beginPath();
  //   ctx.moveTo(endX, endY);
  //   ctx.lineTo(
  //     endX - arrowSize * Math.cos(angle - Math.PI / 6),
  //     endY + arrowSize * Math.sin(angle - Math.PI / 6)
  //   );
  //   ctx.lineTo(
  //     endX - arrowSize * Math.cos(angle + Math.PI / 6),
  //     endY + arrowSize * Math.sin(angle + Math.PI / 6)
  //   );
  //   ctx.closePath();
  //   ctx.fill();

  //   ctx.fillStyle = color;
  //   ctx.font = 'bold 16px Arial';
  //   ctx.fillText(label, endX + 10, endY + 5);
  // }

  onWindowResize = (): void => {
    if (this.isDestroyed) return;
    const container = this.rendererContainer.nativeElement;
    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;
    
    this.canvas.width = width;
    this.canvas.height = height;
    this.axisCanvas.width = width;
    this.axisCanvas.height = height;
    
    this.camera.data.setSize(width, height);
    this.renderer.setSize(width, height);
  };

  animate = (): void => {
    if (this.isDestroyed) return;
    this.animationFrameId = requestAnimationFrame(this.animate);

    this.controls.update();

    const target = (this.controls as any).target || new SPLAT.Vector3(0, 0, 0);
    const camPos = this.camera.position;

    const dir = new SPLAT.Vector3(
      camPos.x - target.x,
      camPos.y - target.y,
      camPos.z - target.z
    );

    const radius = dir.magnitude();
    if (radius === 0) return;

    // === Sferne koordinate ===
    let theta = Math.atan2(dir.x, dir.z);  // trenutni azimuth: -π do π
    let phi = Math.asin(dir.y / radius);

    // === Zapamti početni ugao pri prvom renderovanju ===
    if (this.initialTheta === null) {
      this.initialTheta = theta;
    }

    // === UBACI OVAJ DEO OVDE: Unwraping za kontinuirani theta ===
    if (this.previousTheta === null) {
      this.previousTheta = theta;  // Inicializuj na trenutni theta pri prvom frame-u
    }
    let delta = theta - this.previousTheta;
    if (delta > Math.PI) delta -= 2 * Math.PI;
    if (delta < -Math.PI) delta += 2 * Math.PI;
    theta = this.previousTheta + delta;

    // Definiši granice za clamp na osnovu initialTheta (relativno ograničenje)
    const degToRad = (deg: number) => (deg * Math.PI) / 180;
    const maxOffset = degToRad(60);  // Promeni po potrebi
    const minTheta = this.initialTheta - maxOffset;
    const maxTheta = this.initialTheta + maxOffset;

    // Clampuj unwrapani theta
    theta = Math.max(minTheta, Math.min(maxTheta, theta));
    this.previousTheta = theta;

    // === OGRANIČENJA ===
    // 1. Ograniči azimuth relativno u odnosu na početnu poziciju
    let thetaDiff = theta - this.initialTheta;  // Sada koristi unwrapani theta, pa nema skokova
    
    // Nema više potrebe za while normalizacijom (unwraping je već uradio to)
    // Ograniči na ±60° (već urađeno u clamp-u iznad, ali zadrži za konzistentnost)
    thetaDiff = Math.max(-maxOffset, Math.min(maxOffset, thetaDiff));

    const clampedTheta = this.initialTheta + thetaDiff;

    // 2. Ograniči elevaciju (gore-dole): npr. -30° do +30°
    const minPhi = degToRad(-30);
    const maxPhi = degToRad(30);
    const clampedPhi = Math.max(minPhi, Math.min(maxPhi, phi));

    // === Rekonstruiši smer ===
    const cosPhi = Math.cos(clampedPhi);
    const sinPhi = Math.sin(clampedPhi);
    const sinTheta = Math.sin(clampedTheta);
    const cosTheta = Math.cos(clampedTheta);

    const newDir = new SPLAT.Vector3(
      cosPhi * sinTheta,
      sinPhi,
      cosPhi * cosTheta
    );

    const newPos = new SPLAT.Vector3(
      target.x + newDir.x * radius,
      target.y + newDir.y * radius,
      target.z + newDir.z * radius
    );

    // === Ažuriraj kameru ===
    const forward = newDir.normalize();
    const worldUp = new SPLAT.Vector3(0, 1, 0);
    const right = worldUp.cross(forward).normalize();
    const up = forward.cross(right).normalize();

    const rotMatrix = new SPLAT.Matrix4(
      right.x, up.x, -forward.x, 0,
      right.y, up.y, -forward.y, 0,
      right.z, up.z, -forward.z, 0,
      0, 0, 0, 1
    );

    const rotation = SPLAT.Quaternion.FromMatrix3(rotMatrix);
    this.camera.data.update(newPos, rotation);

    this.renderer.render(this.scene, this.camera);
    // this.drawAxes();
  };
}